// Port of `PublicationDetails` and `ISBN` (SuiteModel/Publication.swift): what a book's copyright page records (one
// JSON value, `settings.publication`), the placeholder values a compile fills in, and ISBN checking.
import { escape } from './proseMarkdown.ts';
import { isCFWhitespaceOrNewline, trimCFWhitespace } from './unicode.ts';

export type Edition = 'paperback' | 'hardcover' | 'ebook' | 'audiobook';
export const EDITIONS: readonly Edition[] = ['paperback', 'hardcover', 'ebook', 'audiobook'];

const LABELS: Record<Edition, string> = { paperback: 'Paperback', hardcover: 'Hardcover', ebook: 'Ebook', audiobook: 'Audiobook' };
export const editionLabel = (e: Edition) => LABELS[e];
/** `Edition.placeholder`: `isbnPaperback`… */
export const editionPlaceholder = (e: Edition) => 'isbn' + LABELS[e];
export const editionFrom = (raw: string | null | undefined): Edition | null =>
  raw != null && (EDITIONS as readonly string[]).includes(raw) ? (raw as Edition) : null;

export interface PublicationDetails {
  publisher: string;
  publisherAddress: string;
  publisherWebsite: string;
  copyrightHolder: string;
  copyrightYear: string;
  firstPublished: string;
  edition: string;
  printingLine: boolean;
  isbns: Record<string, string>;
  editor: string;
  coverDesigner: string;
  coverImage: string;
  assertMoralRights: boolean;
  fictionNotice: boolean;
  rightsNotice: string;
  catalogueRecord: string;
  otherNotices: string;
  authorWebsite: string;
}

export const STANDARD_RIGHTS = 'All rights reserved. No part of this book may be reproduced, stored in a retrieval system or transmitted in any form or by any means, electronic, mechanical, photocopying, recording or otherwise, without the prior written permission of the publisher, except for brief quotations in reviews.';
export const STANDARD_FICTION = "This is a work of fiction. Names, characters, places and incidents are either the products of the author's imagination or used fictitiously. Any resemblance to actual persons, living or dead, events or locales is entirely coincidental.";

export function emptyPublication(): PublicationDetails {
  return {
    publisher: '', publisherAddress: '', publisherWebsite: '', copyrightHolder: '', copyrightYear: '', firstPublished: '',
    edition: '', printingLine: false, isbns: {}, editor: '', coverDesigner: '', coverImage: '', assertMoralRights: false,
    fictionNotice: true, rightsNotice: STANDARD_RIGHTS, catalogueRecord: '', otherNotices: '', authorWebsite: '',
  };
}

/** `init(from:)`: tolerant of missing keys (and of values of the wrong type, which take their default). */
export function decodePublication(json: unknown): PublicationDetails {
  const d = emptyPublication();
  if (!json || typeof json !== 'object') return d;
  const o = json as Record<string, unknown>;
  const text = (k: keyof PublicationDetails) => { if (typeof o[k] === 'string') (d as unknown as Record<string, unknown>)[k] = o[k]; };
  for (const k of ['publisher', 'publisherAddress', 'publisherWebsite', 'copyrightHolder', 'copyrightYear', 'firstPublished', 'edition',
    'editor', 'coverDesigner', 'coverImage', 'rightsNotice', 'catalogueRecord', 'otherNotices', 'authorWebsite'] as const) text(k);
  for (const k of ['printingLine', 'assertMoralRights', 'fictionNotice'] as const) if (typeof o[k] === 'boolean') d[k] = o[k] as boolean;
  if (o.isbns && typeof o.isbns === 'object') {
    for (const [k, v] of Object.entries(o.isbns as Record<string, unknown>)) if (typeof v === 'string') d.isbns[k] = v;
  }
  return d;
}

export function isbnOf(p: PublicationDetails, e: Edition): string { return trimCFWhitespace(p.isbns[e] ?? ''); }

const esc = (text: string) => escape(trimCFWhitespace(text));
const splitLines = (text: string) => text.split(/\r\n|[\n\r\u000b\u000c\u0085\u2028\u2029]/).filter((l) => l !== '');

/** `placeholders(author:year:edition:typeface:)`: the copyright page's values, escaped for prose Markdown. */
export function publicationPlaceholders(p: PublicationDetails, o: { author: string | null; year: number; edition: Edition | null; typeface: string | null;
  /** How many authors `author` names (W9c: a co-written byline, "A and B"); one by default. */
  authors?: number }): Record<string, string> {
  const author = o.author ?? '';
  const holder = p.copyrightHolder === '' ? author : p.copyrightHolder;
  const values: Record<string, string> = {
    publisher: esc(p.publisher),
    publisherAddress: splitLines(p.publisherAddress).map(esc).filter((l) => l !== '').join('\\\n'),
    publisherWebsite: esc(p.publisherWebsite),
    copyrightHolder: esc(holder),
    copyrightYear: esc(p.copyrightYear === '' ? String(o.year) : p.copyrightYear),
    firstPublished: esc(p.firstPublished),
    edition: esc(p.edition),
    printingLine: p.printingLine ? '10 9 8 7 6 5 4 3 2 1' : '',
    isbn: o.edition ? esc(isbnOf(p, o.edition)) : '',
    editor: esc(p.editor),
    coverDesigner: esc(p.coverDesigner),
    coverImage: esc(p.coverImage),
    moralRights: p.assertMoralRights && author !== ''
      ? `The moral right of ${esc(author)} to be identified as the ${(o.authors ?? 1) > 1 ? 'authors' : 'author'} of this work has been asserted.` : '',
    fictionNotice: p.fictionNotice ? STANDARD_FICTION : '',
    rightsNotice: esc(trimAll(p.rightsNotice)),
    catalogueRecord: esc(p.catalogueRecord),
    otherNotices: splitLines(p.otherNotices).map(esc).filter((l) => l !== '').join('\n\n'),
    authorWebsite: esc(p.authorWebsite),
    typeface: esc(o.typeface ?? ''),
  };
  for (const e of EDITIONS) values[editionPlaceholder(e)] = esc(isbnOf(p, e));
  return values;
}

/** Every placeholder the copyright page understands. */
export const PUBLICATION_PLACEHOLDERS: readonly string[] = Object.keys(
  publicationPlaceholders(emptyPublication(), { author: null, year: 2000, edition: null, typeface: null }));

/** `trimmingCharacters(in: .whitespacesAndNewlines)`. */
export function trimAll(s: string): string {
  const cps = [...s];
  let a = 0, b = cps.length;
  while (a < b && isCFWhitespaceOrNewline(cps[a].codePointAt(0)!)) a++;
  while (b > a && isCFWhitespaceOrNewline(cps[b - 1].codePointAt(0)!)) b--;
  return cps.slice(a, b).join('');
}

// MARK: ISBN

export type ISBNCheck =
  | { type: 'empty' }
  | { type: 'valid'; isbn13: string }
  | { type: 'wrongCheckDigit'; expected: string }
  | { type: 'wrongLength'; digits: number }
  | { type: 'notDigits' };

const isDigit = (c: string) => c >= '0' && c <= '9';

export const ISBN = {
  check(text: string): ISBNCheck {
    const raw = [...text].filter((c) => !/\s/u.test(c) && c !== '-').join('').toUpperCase();
    if (raw === '') return { type: 'empty' };
    const chars = [...raw];
    const body = chars.slice(0, -1);
    const last = chars[chars.length - 1];
    if (!body.every(isDigit) || !(isDigit(last) || (chars.length === 10 && last === 'X'))) return { type: 'notDigits' };
    const digits = chars.filter(isDigit).map(Number);
    if (chars.length === 13) {
      const sum = digits.slice(0, 12).reduce((s, d, i) => s + d * (i % 2 === 0 ? 1 : 3), 0);
      const expected = (10 - (sum % 10)) % 10;
      return digits[12] === expected ? { type: 'valid', isbn13: raw } : { type: 'wrongCheckDigit', expected: String(expected) };
    }
    if (chars.length === 10) {
      const sum = digits.slice(0, 9).reduce((s, d, i) => s + d * (10 - i), 0);
      const expected = (11 - (sum % 11)) % 11;
      const actual = last === 'X' ? 10 : digits[9];
      if (actual !== expected) return { type: 'wrongCheckDigit', expected: expected === 10 ? 'X' : String(expected) };
      const twelve = [9, 7, 8, ...digits.slice(0, 9)];
      const sum13 = twelve.reduce((s, d, i) => s + d * (i % 2 === 0 ? 1 : 3), 0);
      return { type: 'valid', isbn13: twelve.join('') + String((10 - (sum13 % 10)) % 10) };
    }
    return { type: 'wrongLength', digits: chars.length };
  },

  /** Why an ISBN doesn't check out; null when it's fine or empty. */
  problem(text: string): string | null {
    const c = ISBN.check(text);
    switch (c.type) {
      case 'empty': case 'valid': return null;
      case 'wrongCheckDigit': return `The last digit should be ${c.expected}. Check the number against your ISBN agency's record.`;
      case 'wrongLength': return `An ISBN has 13 digits (or 10 for older ones); this has ${c.digits}.`;
      case 'notDigits': return 'An ISBN is digits only, with hyphens or spaces if you like (an old 10-digit one may end in X).';
    }
  },
};
